<template>
  <v-dialog v-model="testDialog" fullscreen transition="dialog-bottom-transition" :overlay="false">
    <v-card class="elevation-1 test-rule">
      <v-layout column justify-space-between>
        <v-layout column>
          <!-- Modal header -->
          <v-toolbar color="secondary white--text">
            <v-toolbar-title class="subheading ttu">
              <span v-if="ruleType === 'rule'">
                {{ messages.label.TEST_RULE }}</span>
              <span v-if="ruleType === 'ruleSet'">
                {{ messages.label.TEST_RULE_SET }}</span>
              <span v-if="ruleType === 'ruleCollection'">
                {{ messages.label.TEST_RULE_COLLECTION }}</span>
              <span> - "{{ ruleObj.title }}"</span>
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-toolbar-items>
              <!-- Show Logs Switch -->
              <div class="text-center d-flex align-center mr-2" v-if="isTransformationJSRule">
                <h2 class="body-2 ttu caption mx-2 font-weight-bold">
                  {{ messages.label.SHOW_LOGS }}
                </h2>
                <v-switch v-model="showLogs" hide-details class="pa-0 mx-1" color="accent">
                </v-switch>
                <v-tooltip bottom max-width="200">
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" class="pointer" small color="info">help</v-icon>
                  </template>
                  <span>{{ messages.info.SHOW_LOGS_HELP }}</span>
                </v-tooltip>
              </div>
              <!-- Include Disabled Switch -->
              <div class="text-center d-flex align-center mr-2" v-else>
                <h2 class="body-2 ttu caption mx-2 font-weight-bold">
                  {{ messages.label.INCLUDE_DISABLED }}
                </h2>
                <v-switch v-model="includeDisabled" hide-details class="pa-0 mx-1" color="accent">
                </v-switch>
                <v-tooltip bottom max-width="200">
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" class="pointer" small color="info">help</v-icon>
                  </template>
                  <span>{{ messages.info.INCLUDE_DISABLED_HELP }}</span>
                </v-tooltip>
              </div>
              <v-btn @click.stop="hide()" flat>
                <v-icon class="white--text">close</v-icon>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
          <!-- Main Content -->
          <v-card-text class="pl-3">
            <v-stepper v-model="stepperCounter">
              <v-stepper-header>
                <v-stepper-step :complete="stepperCounter > 1" :editable="stepsEditable" step="1">{{ messages.label.XML
                }}</v-stepper-step>
                <v-divider></v-divider>
                <v-stepper-step :complete="stepperCounter > 2" :editable="stepsEditable" step="2">{{
                    messages.label.RESPONSE
                }}</v-stepper-step>
              </v-stepper-header>
              <v-stepper-items>
                <!-- XML -->
                <v-stepper-content step="1">
                  <v-card class="elevation-0">
                    <v-alert type="error" v-model="errorAlert" dismissible>
                      {{ errorMessage }}
                    </v-alert>
                    <!-- Headers for Transformation JS Rule -->
                    <v-layout row wrap v-if="isTransformationJSRule">
                      <v-flex xs6>
                        <label class="subtitle-2">XML</label>
                      </v-flex>
                      <v-flex xs6>
                        <label class="subtitle-2">ADDITIONAL XML</label>
                      </v-flex>
                    </v-layout>
                    <v-card-text class="ml-0 vop--ace-editor-container" :style="{ height: editorHeight }">
                      <!-- Multiple editors for Transformation JS Rule -->
                      <v-layout row wrap v-show="isTransformationJSRule">
                        <div ref="editorEditableLeft" class="vop--ace-editor vop--ace-editor_left"></div>

                        <div ref="editorEditableRight" class="vop--ace-editor vop--ace-editor_right"></div>
                      </v-layout>
                      <!-- Default editor -->
                      <div v-show="!isTransformationJSRule" ref="editorEditable" class="vop--ace-editor"></div>
                    </v-card-text>
                  </v-card>
                </v-stepper-content>
                <!-- Response -->
                <v-stepper-content step="2">
                  <v-card class="elevation-0">
                    <v-card-title class="pa-0" v-if="displayTabSelection()">
                      <v-spacer></v-spacer>
                      <v-tabs v-model="activeTab">
                        <v-tabs-slider color="accent"></v-tabs-slider>
                        <v-tab v-for="item in responseTabItems" :key="item.title" class="secondary--text"
                          :class="{ 'd-none': !item.visible }">
                          {{ item.title }}
                        </v-tab>
                      </v-tabs>
                    </v-card-title>
                    <v-card-text>
                      <v-tabs-items v-model="activeTab">
                        <!-- Response tab -->
                        <v-tab-item>
                          <v-flex xs12>
                            <v-alert :type="overallStatusType()" :value="true">
                              {{ testResultMessage }}
                            </v-alert>
                          </v-flex>
                          <v-flex xs12 v-if="ruleObj.type === 'VALIDATION'">
                            <!-- FAILED rules -->
                            <div v-if="failedValidations.length > 0">
                              <h2 class="body-2 ttu pa-2">
                                FAILED VALIDATIONS
                              </h2>
                              <RulesTestResultView :rules-test-result="failedValidations" />
                            </div>
                            <!-- PASSED rules -->
                            <div v-if="successfulValidations.length > 0">
                              <h2 class="body-2 ttu pa-2">
                                PASSED VALIDATIONS
                              </h2>
                              <RulesTestResultView :rules-test-result="successfulValidations" />
                            </div>
                          </v-flex>
                          <v-flex xs12 v-if="ruleObj.type === 'TRANSFORMATION'">
                            <!-- FAILED transformations -->
                            <div v-if="failedTransformations.length > 0">
                              <h2 class="body-2 ttu pa-2">
                                FAILED TRANSFORMATIONS
                              </h2>
                              <RulesTestResultView :rules-test-result="failedTransformations" />
                            </div>
                            <!-- PASSED transformations -->
                            <div v-if="transformations.length > 0">
                              <h2 class="body-2 ttu pa-2">
                                TRANSFORMATIONS
                              </h2>
                              <RulesTestResultView :rules-test-result="transformations" />
                            </div>
                          </v-flex>
                          <v-flex xs12 v-if="ruleObj.type === 'TRANSFORMATION_JS'">
                            <v-card class="elevation-0">
                              <v-card-text class="ml-0 vop--ace-editor-container" :style="{ height: editorHeight }">
                                <div ref="editorReadOnly" class="vop--ace-editor"></div>
                              </v-card-text>
                            </v-card>
                          </v-flex>
                        </v-tab-item>
                        <!-- ADI Diff tab -->
                        <v-tab-item :class="{
                          'd-none': ruleObj.type !== 'TRANSFORMATION'
                        }">
                          <v-card :style="{ height: editorHeight }">
                            <div class="vop--acediff-editor-container">
                              <div>
                                <div class="vop--acediff-editor" ref="diffEditorLeft"></div>
                              </div>
                              <div class="vop--acediff-gutter" ref="diffGutter"></div>
                              <div>
                                <div class="vop--acediff-editor" ref="diffEditorRight"></div>
                              </div>
                            </div>
                          </v-card>
                        </v-tab-item>
                        <!-- Logs -->
                        <v-tab-item :class="{
                          'd-none': ruleObj.type !== 'TRANSFORMATION_JS'
                        }">
                          <v-data-table :headers="logHeaders" :items="javascriptLogs" class="elevation-1">
                            <template v-slot:items="props">
                              <td>{{ props.item.log }}</td>
                            </template>
                          </v-data-table>
                        </v-tab-item>
                      </v-tabs-items>
                    </v-card-text>
                  </v-card>
                </v-stepper-content>
              </v-stepper-items>
            </v-stepper>
          </v-card-text>
        </v-layout>
        <v-card-actions class="grey lighten-4 py-2">
          <v-btn color="grey lighten-1 white--text" @click="hide()">
            {{ messages.label.CANCEL }}
          </v-btn>
          <v-btn color="primary" :disabled="testInProgress" :loading="testInProgress" @click="test()">
            {{ messages.label.TEST }}
          </v-btn>
        </v-card-actions>
      </v-layout>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapGetters } from "vuex"
import ace from "brace"
import AceDiff from "brace-diff"
import uniqid from "uniqid"
import xmlFormatter from "xml-formatter"
import "brace/mode/xml"
import "brace/mode/json"
import "brace/theme/eclipse"
import "brace/ext/searchbox"
import AppService from "@/AppService"
import * as metadataRulesDataService from "@/MetadataRules/MetadataRulesDataService"
import MetadataRulesService from "@/MetadataRules/MetadataRulesService"
import RulesTestResultView from "@/MetadataRules/RulesTestResultView"
import * as utils from "@/Shared/utils"
import messages from "@/MetadataRules/messages.json"
import {
  MVATS_WEB_SERVICE_URL,
  RULE_MGMT_DATA_SERVICE_URL,
  MVATS_TRANSFORMATION_SERVICE_URL
} from "@/store/constants/getter-types"

/**
 *
 */
function initialData(instance) {
  return {
    ruleType: "rule",
    stepperCounter: 0,
    testXML: "",
    testXMLLeft: "",
    testXMLRight: "",
    activeTab: null,
    testInProgress: false,
    errorAlert: false,
    errorMessage: "",
    stepsEditable: false,
    testResultMessage: "N/A",
    testStatus: "",
    successfulValidations: [],
    failedValidations: [],
    transformations: [],
    failedTransformations: [],
    responseTabItems: instance.getResponseTabItems(),
    includeDisabled: true,
    showLogs: true,
    isTransformationJSRule: false,
    logHeaders: instance.getLogHeaders(),
    javascriptLogs: []
  }
}
export default {
  name: "MetadataRuleTest",
  components: {
    RulesTestResultView
  },
  props: {
    value: Boolean,
    ruleObj: Object
  },
  data: function () {
    return initialData(this)
  },
  watch: {
    testDialog(newValue) {
      if (newValue === true) {
        this.show()
      }
    }
  },
  computed: {
    ...mapGetters([
      MVATS_WEB_SERVICE_URL,
      RULE_MGMT_DATA_SERVICE_URL,
      MVATS_TRANSFORMATION_SERVICE_URL
    ]),
    ...mapState(["mpxParentAccount"]),
    testDialog: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit("input", value)
      }
    },
    editorHeight() {
      let height = 50
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          height = 65
          break
        case "sm":
          height = 65
          break
        case "md":
          height = 70
          break
        case "lg":
          height = 70
          break
        case "xl":
          height = 75
          break
      }
      if (this.errorAlert) {
        height = height - 8
      }
      return `${height}vh`
    }
  },
  methods: {
    /**
     * reset
     */
    reset() {
      Object.assign(this.$data, initialData())
    },
    /**
     * hide - hide test modal
     */
    hide() {
      this.resetTestData()
      this.testDialog = false
    },

    /**
     * show - Show Test Rule / Rule Set / Rule Collection Modal
     */
    show() {
      this.resetTestData()
      this.parseRuleType()
      this.initXMLEditor()
    },

    /**
     * Re-initialize
     */
    resetTestData() {
      this.ruleType = "rule"
      this.activeTab = null
      this.testXML = ""
      this.resultXML = ""
      this.testInProgress = false
      this.errorAlert = false
      this.errorMessage = ""
      this.testResultMessage = "N/A"
      this.testStatus = ""
      this.successfulValidations = []
      this.failedValidations = []
      this.failedTransformations = []
      this.transformations = []
      this.stepperCounter = 1
      this.includeDisabled = true
      this.isTransformationJSRule = false
      this.javascriptLogs = []
    },

    /**
     * Tab items list
     */
    getResponseTabItems() {
      return [
        { title: "RESULTS", visible: false },
        { title: "ADI DIFF.", visible: false },
        { title: "LOGS", visible: false }
      ]
    },

    /**
     * Identifies if we need to display tab selection
     */
    displayTabSelection() {
      if (
        (this.ruleObj.type === "TRANSFORMATION" &&
          this.testStatus !== "NOT_ATTEMPTED") ||
        (this.ruleObj.type === "TRANSFORMATION_JS" && this.showLogs)
      ) {
        return true
      } else {
        return false
      }
    },

    /**
     * Update tab visibility
     * NOTE:  We need to do this lengthy logic as dynamic tabs doesn't work correct in current version of Vuetify (1.5)
     */
    updateTabsVisibility() {
      if (
        this.ruleObj.type === "TRANSFORMATION" &&
        this.testStatus !== "NOT_ATTEMPTED"
      ) {
        this.responseTabItems[0].visible = true // Results
        this.responseTabItems[1].visible = true // ADI Diff
        this.responseTabItems[2].visible = false // Logs
      } else if (this.ruleObj.type === "TRANSFORMATION_JS" && this.showLogs) {
        this.responseTabItems[0].visible = true // Results
        this.responseTabItems[1].visible = false // ADI Diff
        this.responseTabItems[2].visible = true // Logs
      }
    },

    /**
     * Log view headers
     */
    getLogHeaders() {
      return [
        {
          text: "Javascript Logs",
          align: "left",
          sortable: false
        }
      ]
    },

    /**
     * parseRuleType
     */
    parseRuleType() {
      if (!this.$lodash.isUndefined(this.ruleObj)) {
        let isRuleCollection = this.ruleObj.id.indexOf("/RuleCollection/") > 0
        let isRuleSet = this.ruleObj.id.indexOf("/RuleSet/") > 0
        let isRule = this.ruleObj.id.indexOf("/Rule/") > 0
        this.isTransformationJSRule = this.ruleObj.type === "TRANSFORMATION_JS"

        if (isRuleCollection) {
          this.ruleType = "ruleCollection"
        } else if (isRuleSet) {
          this.ruleType = "ruleSet"
        } else if (isRule) {
          this.ruleType = "rule"
        }
      }
    },

    /**
     * initXMLEditor
     */
    initXMLEditor() {
      if (this.isTransformationJSRule) {
        // Left
        let editorLeft
        editorLeft = ace.edit(this.$refs.editorEditableLeft)
        editorLeft.setTheme("ace/theme/eclipse")
        editorLeft.getSession().setMode("ace/mode/xml")
        editorLeft.session.setValue(this.testXMLLeft)

        // Additional
        let editorRight
        editorRight = ace.edit(this.$refs.editorEditableRight)
        editorRight.setTheme("ace/theme/eclipse")
        editorRight.getSession().setMode("ace/mode/xml")
        editorRight.session.setValue(this.testXMLRight)
      } else {
        let editor
        editor = ace.edit(this.$refs.editorEditable)
        editor.setTheme("ace/theme/eclipse")
        editor.getSession().setMode("ace/mode/xml")
        editor.session.setValue(this.testXML)
      }
    },

    /**
     * initXMLEditor
     */
    initReadOnlyResponseView(response, type) {
      let editor
      editor = ace.edit(this.$refs.editorReadOnly)
      editor.setReadOnly(true)
      editor.setTheme("ace/theme/eclipse")

      if (type === "json") {
        editor.getSession().setMode("ace/mode/json")
        editor.session.setValue(response)
      } else {
        editor.getSession().setMode("ace/mode/xml")
        editor.session.setValue(response)
      }
    },

    /**
     * test - test rule / rule set / rule collection
     */
    test() {
      const VALIDATION = "VALIDATION"
      const TRANSFORMATION = "TRANSFORMATION"
      const TRANSFORMATION_JS = "TRANSFORMATION_JS"
      console.log()

      // Basic validation
      if (this.isTransformationJSRule) {
        let editorLeft = ace.edit(this.$refs.editorEditableLeft)
        this.testXMLLeft = editorLeft.getValue()
        let editorRight = ace.edit(this.$refs.editorEditableRiight)
        this.testXMLRight = editorRight.getValue()

        if (this.$lodash.isEmpty(this.testXMLLeft)) {
          this.errorAlert = true
          this.errorMessage = this.messages.error.VALID_XML
          return
        }
      } else {
        let editor = ace.edit(this.$refs.editorEditable)
        this.testXML = editor.getValue()
        if (this.$lodash.isEmpty(this.testXML)) {
          this.errorAlert = true
          this.errorMessage = this.messages.error.VALID_XML
          return
        }
      }

      this.testInProgress = true
      let testRequest = {
        xml: this.testXML,
        guid: this.ruleObj.guid,
        secondaryOwner: this.ruleObj.secondaryOwner,
        includeDisabled: this.includeDisabled
      }
      let testPromise = {}
      let accountContext = this.ruleObj.ownerId

      switch (this.ruleType) {
        case "rule":
          if (this.ruleObj.type === VALIDATION) {
            testPromise = metadataRulesDataService.testValidationRule(
              testRequest,
              accountContext,
              this.MVATS_WEB_SERVICE_URL
            )
          } else if (this.ruleObj.type === TRANSFORMATION) {
            testPromise = metadataRulesDataService.testTransformationRule(
              testRequest,
              accountContext,
              this.MVATS_WEB_SERVICE_URL
            )
          } else if (this.ruleObj.type === TRANSFORMATION_JS) {
            testRequest = {}
            testRequest = {
              metadata: this.testXMLLeft,
              javascript: this.ruleObj.actions[0],
              returnLogs: this.showLogs
            }
            // Send additional metadata if added
            if (!this.$lodash.isEmpty(this.testXMLRight)) {
              testRequest["additionalMetadata"] = this.testXMLRight
            }
            testPromise = metadataRulesDataService.testTransformationJSRule(
              testRequest,
              accountContext,
              this.MVATS_TRANSFORMATION_SERVICE_URL
            )
          }
          break
        case "ruleSet":
          if (this.ruleObj.type === VALIDATION) {
            testPromise = metadataRulesDataService.testValidationRuleSet(
              testRequest,
              accountContext,
              this.MVATS_WEB_SERVICE_URL
            )
          } else if (this.ruleObj.type === TRANSFORMATION) {
            testPromise = metadataRulesDataService.testTransformationRuleSet(
              testRequest,
              accountContext,
              this.MVATS_WEB_SERVICE_URL
            )
          }
          break
        case "ruleCollection":
          if (this.ruleObj.type === VALIDATION) {
            testPromise = metadataRulesDataService.testValidationRuleCollection(
              testRequest,
              accountContext,
              this.MVATS_WEB_SERVICE_URL
            )
          } else if (this.ruleObj.type === TRANSFORMATION) {
            testPromise = metadataRulesDataService.testTransformationRuleCollection(
              testRequest,
              accountContext,
              this.MVATS_WEB_SERVICE_URL
            )
          }
          break
      }

      testPromise
        .then(result => {
          let testResult = result.data
          this.stepsEditable = true
          this.testResultMessage = testResult.message
            ? testResult.message
            : "N/A"
          this.testStatus = testResult.status
          this.stepperCounter = 2
          this.activeTab = 0

          if (this.ruleObj.type === TRANSFORMATION) {
            console.log(testResult)
            if (!this.$lodash.isUndefined(testResult.transformations)) {
              this.transformations = MetadataRulesService.addIds(
                testResult.transformations
              )
            }
            if (!this.$lodash.isUndefined(testResult.failedTransformations)) {
              this.failedTransformations = MetadataRulesService.addIds(
                testResult.failedTransformations
              )
            }
            this.resultXML = testResult.xml
            this.displayDifferences()
          } else if (this.ruleObj.type === TRANSFORMATION_JS) {
            let responseMetadata = testResult.metadata
            try {
              JSON.parse(responseMetadata)
              this.initReadOnlyResponseView(responseMetadata, "json")
            } catch (e) {
              // If unable to parse the response as json, display in XML mode
              this.initReadOnlyResponseView(responseMetadata, "xml")
            }

            if (this.showLogs) {
              this.javascriptLogs = this.constructLogItems(testResult.logs)
            }
          } else {
            if (!this.$lodash.isUndefined(testResult.successfulValidations)) {
              this.successfulValidations = MetadataRulesService.addIds(
                testResult.successfulValidations
              )
            }
            if (!this.$lodash.isUndefined(testResult.failedValidations)) {
              this.failedValidations = MetadataRulesService.addIds(
                testResult.failedValidations
              )
            }
          }
          this.updateTabsVisibility()
        })
        .catch(error => {
          this.showError(error)
        })
        .finally(() => {
          this.testInProgress = false
        })
    },

    /**
     * Construct log items for display
     */
    constructLogItems(logItems) {
      let logItemsResponse = []
      this.$lodash.forEach(logItems, item => {
        let logItem = {
          id: "",
          log: ""
        }
        logItem["id"] = uniqid()
        logItem["log"] = item
        logItemsResponse.push(logItem)
      })
      return logItemsResponse
    },

    /**
     * Display differences between original and modified XML version
     */
    displayDifferences() {
      // FIXME: something is not right here, ace diff is not displaying differeces
      var options = {
        indentation: " ",
        stripComments: true,
        collapseContent: true
      }
      let modifiedXML = xmlFormatter(this.resultXML, options)
      let originalXML = xmlFormatter(this.testXML, options)
      let aceDiffer = new AceDiff({
        mode: "ace/mode/xml",
        showDiffs: true,
        showConnectors: true,
        gutterEl: this.$refs.diffGutter,
        left: {
          element: this.$refs.diffEditorLeft,
          theme: "ace/theme/eclipse",
          editable: false,
          copyLinkEnabled: false
        },
        right: {
          element: this.$refs.diffEditorRight,
          theme: "ace/theme/eclipse",
          editable: false,
          copyLinkEnabled: false
        }
      })
      let editors = aceDiffer.getEditors()
      // Set Value to the editors
      this.$nextTick(() => {
        editors.left.setValue(originalXML, 1)
        editors.right.setValue(modifiedXML, 1)
        aceDiffer.diff()
      })
    },

    /**
     * showError - Handles displaying error
     * @param {object} error
     */
    showError(error) {
      let isServerError = utils.isMPXError(error)
      let isClientError = utils.isClientError(error)
      if (isServerError) {
        this.errorAlert = true
        this.errorMessage = error.response.data.description
      } else if (isClientError) {
        this.errorAlert = true
        this.errorMessage = error.message
      }
    },

    /**
     * overallStatusType
     */
    overallStatusType() {
      if (this.testStatus === "NOT_ATTEMPTED") {
        return "warning"
      } else if (this.testStatus === "ERROR") {
        return "error"
      } else {
        return "success"
      }
    }
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    /**
     * Events
     */
    AppService.$on("event:mpxAccountChanged", this.reset)
  },
  beforeDestroy() {
    AppService.$off("event:mpxAccountChanged", this.reset)
  }
}
</script>

<style lang="scss" scoped>
.vop--ace-editor-container {
  position: relative;
}

.vop--ace-editor {
  position: absolute;
  top: 0px;
  bottom: 0px;
  left: 0px;
  right: 0px;
}

.vop--ace-editor_left {
  width: 46vw;
}

.vop--ace-editor_right {
  left: 46vw;
}

.vop--ace-editor .ace_search_field,
.ace_searchbtn:last-child {
  color: black !important;
}

.vop--acediff-editor-container {
  display: flex;
  display: -webkit-flex;
  flex-direction: row;
  position: absolute;
  bottom: 0;
  width: 100%;
  top: 0px !important;
  left: 0px;
  height: 100%;
  overflow: auto;
}

.vop--acediff-editor-container>div {
  flex-grow: 1;
  -webkit-flex-grow: 1;
  position: relative;
}

.vop--acediff-editor-container>div.vop--acediff-gutter {
  flex: 0 0 60px;
  -webkit-flex: 0 0 60px;
  border-left: 1px solid #999999;
  border-right: 1px solid #999999;
  background-color: #efefef;
  overflow: hidden;
}

.vop--acediff-gutter svg {
  background-color: #efefef;
}

.vop--acediff-editor {
  position: absolute !important;
  top: 0;
  bottom: 0;
  width: 100%;
}

/** acediff specific styles, need to move in to a commmon style sheet */
.acediff-diff {
  background-color: #d8f2ff;
  border-top: 1px solid #a2d7f2;
  border-bottom: 1px solid #a2d7f2;
  position: absolute;
  z-index: 4;
}

.acediff-diff.targetOnly {
  height: 0px !important;
  border-top: 1px solid #a2d7f2;
  border-bottom: 0px;
  position: absolute;
}

.acediff-connector {
  fill: #d8f2ff;
  stroke: #a2d7f2;
}

.test-rule {
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>



// WEBPACK FOOTER //
// src/MetadataRules/MetadataRuleTest.vue